import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { ArrowRight, GraduationCap, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { studentsAPI } from '../lib/api';
import { useCohortOptions } from './CohortFields';

/** Promote a whole class (intake year + course type) to the next year of study. */
export const PromoteCohortDialog = ({ open, onOpenChange, onDone }) => {
  const { courseTypes, years } = useCohortOptions();
  const [year, setYear] = useState('');
  const [courseType, setCourseType] = useState('');
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [graduate, setGraduate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setPreview(null); setSelected(new Set()); setGraduate(false); return; }
    if (!year && years.length) setYear(String(years[0]));
    if (!courseType && courseTypes.length) setCourseType(courseTypes[0].name);
  }, [open, years, courseTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !year || !courseType) return;
    setLoading(true);
    studentsAPI.promotePreview({ year_of_enrollment: parseInt(year, 10), course_type: courseType })
      .then((r) => {
        setPreview(r.data);
        setSelected(new Set(r.data.students.filter((s) => !s.final_year && s.status === 'active').map((s) => s.id)));
      })
      .catch(() => toast.error('Failed to load class'))
      .finally(() => setLoading(false));
  }, [open, year, courseType]);

  const rows = preview?.students || [];
  const promotable = rows.filter((s) => !s.final_year);
  const finals = rows.filter((s) => s.final_year);
  const toggle = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSel = promotable.length > 0 && promotable.every((s) => selected.has(s.id));

  const handlePromote = async () => {
    setSaving(true);
    try {
      const res = await studentsAPI.promote({ student_ids: [...selected], graduate_ids: graduate ? finals.map((s) => s.id) : [] });
      toast.success(`Promoted ${res.data.promoted} student${res.data.promoted === 1 ? '' : 's'}${res.data.graduated ? ` · ${res.data.graduated} marked graduated` : ''}`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Promotion failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="promote-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-ica-navy flex items-center gap-2"><TrendingUp className="w-5 h-5 text-ica-gold" /> Promote cohort</DialogTitle>
          <DialogDescription>Move a whole class to its next year of study so new placement cycles pick them up automatically. Past placements are untouched.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Class of</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger data-testid="promote-year"><SelectValue placeholder="Intake year" /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)} data-testid={`promote-year-opt-${y}`}>Class of {y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Course type</Label>
            <Select value={courseType} onValueChange={setCourseType}>
              <SelectTrigger data-testid="promote-course-type"><SelectValue placeholder="Course type" /></SelectTrigger>
              <SelectContent>{courseTypes.map((c) => <SelectItem key={c.name} value={c.name} data-testid={`promote-course-type-opt-${c.name}`}>{c.name} · {c.course_length}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {loading ? <p className="text-sm text-gray-500 py-4 text-center">Loading class…</p> : preview && (
          <div className="space-y-3">
            {rows.length === 0 ? (
              <p className="text-sm text-gray-500 italic py-3" data-testid="promote-empty">No students found for this class.</p>
            ) : (
              <>
                <div className="border rounded-md">
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b text-xs font-semibold uppercase text-gray-500">
                    <input type="checkbox" data-testid="promote-select-all" checked={allSel} onChange={() => setSelected(allSel ? new Set() : new Set(promotable.map((s) => s.id)))} className="h-4 w-4 accent-[#1e3a5f]" />
                    <span>Student</span><span className="ml-auto">Year of study</span>
                  </div>
                  <ul className="divide-y max-h-72 overflow-y-auto" data-testid="promote-list">
                    {rows.map((s) => (
                      <li key={s.id} data-testid={`promote-row-${s.id}`} className={`flex items-center gap-2 px-3 py-2 text-sm ${s.final_year ? 'bg-amber-50/50' : ''}`}>
                        <input type="checkbox" data-testid={`promote-check-${s.id}`} disabled={s.final_year} checked={selected.has(s.id)} onChange={() => toggle(s.id)} className="h-4 w-4 accent-[#1e3a5f] disabled:opacity-40" />
                        <span className="min-w-0 flex-1 truncate">{s.name} <span className="text-gray-400 text-xs">· {s.student_number}</span>
                          {s.status !== 'active' && <span className="ml-1 text-[10px] uppercase font-semibold text-gray-500 bg-gray-100 rounded px-1">{s.status}</span>}
                        </span>
                        {s.final_year ? (
                          <span className="text-xs text-amber-700 font-medium inline-flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" /> {s.from_year} · final year</span>
                        ) : (
                          <span className="text-xs text-gray-600 inline-flex items-center gap-1">{s.from_year} <ArrowRight className="w-3 h-3 text-ica-gold" /> <b className="text-ica-navy">{s.to_year}</b></span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
                {finals.length > 0 && (
                  <label className="flex items-start gap-2 text-sm rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 cursor-pointer" data-testid="promote-graduate-option">
                    <input type="checkbox" checked={graduate} onChange={(e) => setGraduate(e.target.checked)} className="h-4 w-4 mt-0.5 accent-[#1e3a5f]" data-testid="promote-graduate-check" />
                    <span><b>{finals.length}</b> student{finals.length === 1 ? ' is' : 's are'} already in the final year of this {preview.course_length} course — mark {finals.length === 1 ? 'them' : 'them'} as <b>graduated</b> instead.</span>
                  </label>
                )}
              </>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="promote-cancel">Cancel</Button>
          <Button onClick={handlePromote} disabled={saving || (selected.size === 0 && !(graduate && finals.length))} data-testid="promote-confirm">
            <TrendingUp className="w-4 h-4 mr-2" /> {saving ? 'Promoting…' : `Promote ${selected.size}${graduate && finals.length ? ` · graduate ${finals.length}` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PromoteCohortDialog;
